import { Modal, Button } from 'rsuite';
import Select, { components } from 'react-select';
import { useState } from 'react';
import { TbGridDots } from 'react-icons/tb';
import { HiChevronDown } from 'react-icons/hi';

const ALL_OPTION = { value: '__all__', label: 'All' };

function ColumnModal({ show, handleClose, handleModalOk, columns, loader }) {
    const [selectedColumn, setSelectedColumn] = useState([])
    const [isFocusedColumn, setIsFocusedColumn] = useState(false)
    const [selectError, setSelectError] = useState(false)

    const columnOptions = (columns ?? []).filter((col) => col.id !== "S.NO").map(col => ({
        value: col.id,
        label: col.name,
    }))

    // True when every real column is selected ("All" chip is never stored in selectedColumn)
    const allSelected = columnOptions.length > 0 &&
        columnOptions.every(opt => selectedColumn.some(s => s.value === opt.value));

    // Hide "All" from dropdown once every column is selected; show it again when any column is removed
    const optionsWithAll = allSelected ? columnOptions : [ALL_OPTION, ...columnOptions];

    const handleChange = (selected) => {
        if (!selected || selected.length === 0) {
            setSelectedColumn([]);
            if (selectError) setSelectError(false);
            return;
        }

        const selectedValues = selected.map(s => s.value);
        const allClicked = selectedValues.includes('__all__');

        if (allClicked) {
            if (allSelected) {
                // "All" was already active — clicking it again deselects everything
                setSelectedColumn([]);
            } else {
                // Select all real columns only (no ALL_OPTION chip)
                setSelectedColumn(columnOptions);
            }
        } else {
            // Normal multi-select — keep only real options
            setSelectedColumn(selected.filter(s => s.value !== '__all__'));
        }

        if (selectError) setSelectError(false);
    };

    const handleExport = () => {
        if (selectedColumn.length === 0) {
            setSelectError(true);
            return;
        }
        handleModalOk(selectedColumn, setSelectedColumn);
        setSelectError(false);
    };

    const handleCancel = () => {
        setSelectError(false);
        handleClose(setSelectedColumn);
    };

    const DropdownIndicator = ({ state, ...props }) => {
        return (
            <components.DropdownIndicator {...props}>
                {selectedColumn && selectedColumn.length > 1 ? (
                    <TbGridDots style={{ fontSize: "1.5em" }} />
                ) : (
                    <HiChevronDown style={{ fontSize: "1.5em" }} />
                )}
            </components.DropdownIndicator>
        );
    };

    return (
        <Modal size='xs' open={show} onClose={handleCancel} centered backdrop="static">
            <Modal.Header>
                <Modal.Title> Select Columns to Export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Select
                        tabSelectsValue={false}
                        isMulti
                        closeMenuOnSelect={false}
                        options={optionsWithAll}
                        className="select-search"
                        components={{ DropdownIndicator: (props) => <DropdownIndicator {...props} state="columns" /> }}
                        classNamePrefix="select"
                        placeholder="Columns"
                        isClearable
                        name="columns"
                        onFocus={() => setIsFocusedColumn(true)}
                        onBlur={() => setIsFocusedColumn(false)}
                        onChange={handleChange}
                        value={selectedColumn}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            container: (styles) => ({
                                ...styles,
                                borderRadius: "9px",
                                border: selectError ? "1.5px solid #e74c3c" : "none",
                            }),
                            control: (styles, state) => ({
                                ...styles,
                                backgroundColor: "hsl(204deg 33.33% 97.06%)",
                                borderStyle: "none",
                                boxShadow: "none",
                                borderRadius: "10px",
                                maxHeight: state.isFocused ? "auto" : "30px",
                            }),
                            indicatorSeparator: styles => ({
                                ...styles, width: "0px",
                                minHeight: "20px",
                            }),
                            valueContainer: (styles) => ({
                                ...styles,
                                maxHeight: isFocusedColumn ? "auto" : "30px",
                            }),
                            option: (styles, { data }) => ({
                                ...styles,
                                fontWeight: data.value === '__all__' ? '600' : 'normal',
                            }),
                        }}
                    />
                    {selectError && (
                        <p style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '6px', marginBottom: 0 }}>
                            Please select at least one column to export.
                        </p>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    disabled={loader}
                    onClick={handleExport}
                    style={{ backgroundColor: '#1ba1a7', color: "white" }}
                >
                    Export
                </Button>
                <Button onClick={handleCancel} appearance="subtle">
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ColumnModal